import { useAppConfiguration } from '#app/hooks/useAppConfiguration.js';
import {
  fetchQuery,
  type GraphQLTaggedNode,
  type OperationType,
  useRelayEnvironment,
  type VariablesOf,
} from '@contra/relay';
import { duration, HumanDuration } from '@contra/utilities/duration';
import { useCallback, useEffect, useRef, useState } from 'react';

export type PollingConfig<T extends OperationType = OperationType> =
  T extends OperationType
    ? {
        /**
         * The cache config to pass to fetchQuery
         */
        cacheConfig?: Parameters<typeof fetchQuery>[3];
        /**
         * Whether polling should be disabled
         */
        disable?: boolean;
        /**
         * Called when the polling query returns data
         */
        onData?: (data: PollingData<T>) => void;
        /**
         * Called when the polling query returns an error
         */
        onError?: (error: Error) => void;
        /**
         * The polling interval when the window is in focus
         */
        pollingInterval: HumanDuration;
        /**
         * The polling interval when the window is not in focus
         */
        pollingIntervalWhenOutOfFocus: HumanDuration;
        /**
         * The query to poll
         */
        query: GraphQLTaggedNode;
        /**
         * The variables to pass to the query
         */
        variables: VariablesOf<T>;
      }
    : never;

type PollingData<T extends OperationType> = T['response'];

const defaultPollingConfig: Pick<
  PollingConfig,
  'disable' | 'pollingInterval' | 'pollingIntervalWhenOutOfFocus'
> = {
  disable: false,
  pollingInterval: '30 seconds',
  pollingIntervalWhenOutOfFocus: '5 minutes',
};

/**
 * Polls the provided query at the provided interval, calling the provided callbacks when the query returns data or an error.
 * @param config {PollingConfig} The configuration for polling
 * @param config.disable {boolean} (optional, default = false) Whether to disable polling
 * @param config.onData {function} (optional) Called when the polling query returns data
 * @param config.onError {function} (optional) Called when the polling query returns an error
 * @param config.query {GraphQLTaggedNode} (required) The query to run with useLazyLoadQuery
 * @param config.pollingInterval {HumanDuration} (optional, default = 30_000) The interval to poll at when the window is in focus
 * @param config.pollingIntervalWhenOutOfFocus {HumanDuration} (optional, default = 300_000) The interval to poll at when the window is not in focus
 * @param config.variables {VariablesOf} (required) The variables to pass to useLazyLoadQuery
 * @param config.cacheConfig {CacheConfig} (optional) The cache config to pass to fetchQuery
 * @returns {PollingData} The data returned by the query
 */
export const usePolling = <T extends OperationType = OperationType>(
  config: PollingConfig<T>,
): void => {
  const {
    cacheConfig,
    disable,
    onData,
    onError,
    pollingInterval,
    pollingIntervalWhenOutOfFocus,
    query,
    variables,
  } = {
    ...defaultPollingConfig,
    ...config,
  };

  const [isInFocus, setIsInFocus] = useState(true);
  const intervalId = useRef(0);
  const environment = useRelayEnvironment();

  const onBlur = useCallback(() => {
    setIsInFocus(false);
  }, []);

  const onFocus = useCallback(() => {
    setIsInFocus(true);
  }, []);

  useEffect(() => {
    window.addEventListener('blur', onBlur);
    window.addEventListener('focus', onFocus);

    return () => {
      window.removeEventListener('blur', onBlur);
      window.removeEventListener('focus', onFocus);
    };
  }, [onBlur, onFocus]);

  const appConfiguration = useAppConfiguration();

  let activePollingInterval = isInFocus
    ? duration(pollingInterval, 'milliseconds')
    : duration(pollingIntervalWhenOutOfFocus, 'milliseconds');

  if (appConfiguration.playwrightTest) {
    // https://contra-work.slack.com/archives/C016ND05C2Z/p1727313897855109?thread_ts=1727303889.615619&cid=C016ND05C2Z
    activePollingInterval = duration('1 second', 'milliseconds');
  }

  useEffect(() => {
    if (disable) {
      clearInterval(intervalId.current);
    } else {
      if (intervalId.current) {
        clearInterval(intervalId.current);
      }

      const intervalCallback = () => {
        fetchQuery(environment, query, variables, cacheConfig)
          .toPromise()
          .then((response) => {
            onData?.(response);
          })
          .catch((error_) => {
            onError?.(error_);
          });
      };

      intervalId.current = window.setInterval(() => {
        intervalCallback();
      }, activePollingInterval);

      // Leading invocation to ensure that the data is fetched as soon as the component mounts
      intervalCallback();
    }

    return () => clearInterval(intervalId.current);
  }, [
    activePollingInterval,
    cacheConfig,
    disable,
    environment,
    intervalId,
    isInFocus,
    onData,
    onError,
    query,
    variables,
  ]);
};
